import React, { useState } from 'react';
import { Row, Col, Tabs, Grid } from 'antd';
import NoteCard from './NoteCard';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../analytics';
import NoteInsights from './NoteInsights';
import NotePatient from './NotePatient';
import NoteDrugInteractions from './NoteDrugInteractions';

const { useBreakpoint } = Grid;

// Note types that should not display the patient instructions tab
const NOTE_TYPES_WITHOUT_PATIENT_INSTRUCTIONS = ['professional_meeting'];

const NoteDetailsTabs = ({ source, onPatientLinked, onPatientCreated, onPatientUpdated }) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const [activeTab, setActiveTab] = useState('soapNote');

  const activeDoctorNote = source.getLastDoctorNote();
  const activePatientInstructionsNote = source.getLastPatientInstructionsNote();

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'patientInstructions') {
      logEvent('PatientInstructionsTab', 'Open');
    }
  };

  const widgetsCol = (
    <Col xs={24} sm={24} md={24} lg={8}>
      <div style={{ marginTop: screens.lg ? '40px' : '10px' }}>
        {source.displayWidgets() && activeDoctorNote.isValid() && (
          <>
            <NotePatient
              patient={source.getPatient()}
              source={source}
              note={activeDoctorNote}
              onPatientLinked={onPatientLinked}
              onPatientCreated={onPatientCreated}
              onPatientUpdated={onPatientUpdated}
            />
            {Array.isArray(activeDoctorNote.getMedications()) && activeDoctorNote.getMedications().length > 0 && (
              <NoteDrugInteractions 
                key={source.getPatient() ? `drug-interactions-for-patient-${source.getPatient().getUUID()}` : 'drug-interactions-no-patient'}
                note={activeDoctorNote} 
                source={source} 
              />
            )}
          </>
        )}
        {/* Only show insights if the note has insights and the session has a patient */}
        {activeDoctorNote.hasInsights() && source.hasPatient() && (
          <NoteInsights note={activeDoctorNote} />
        )}
      </div>
    </Col>
  );

  const tabsCol = (
    <Col xs={24} sm={24} md={24} lg={16}>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        type="card"
        animated
        className="note-details--tabs"
        items={[
          {
            key: 'soapNote',
            label: source.getLastDoctorNoteTypeDisplay(),
            children: (
              <div className="note-details--tab-content" data-testid="soap-tab">
                <NoteCard note={activeDoctorNote} source={source} />
              </div>
            )
          },
          activePatientInstructionsNote && !NOTE_TYPES_WITHOUT_PATIENT_INSTRUCTIONS.includes(source.getLastDoctorNoteType()) && {
            key: 'patientInstructions',
            label: t('session_details.fields.tab_patient_instructions_note'),
            children: (
              <div className="note-details--tab-content" data-testid="patient-instructions-tab">
                <NoteCard note={activePatientInstructionsNote} source={source} />
              </div>
            )
          }
        ].filter(Boolean)}
      />
    </Col>
  );

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      {!screens.lg && !source.isPaywalled() && widgetsCol}
      {tabsCol}
      {screens.lg && !source.isPaywalled() && widgetsCol}
    </Row>
  );
};

export default NoteDetailsTabs;
